import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from 'recharts';
import { X, Loader2, ChevronRight, ChevronDown, Eye } from 'lucide-react';
import StatCard from '../shared/StatCard';
import DateRangeFilter from '../shared/DateRangeFilter';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL } from '@shared/constants';
import { useOptions } from '../../context/OptionsContext';
import client from '../../api/client';

const CHART_COLORS = [
  '#d97706', '#2563eb', '#16a34a', '#dc2626', '#7c3aed',
  '#0891b2', '#ca8a04', '#be185d', '#059669', '#4f46e5',
];

const STATUS_FILTERS = [
  { value: 'all',                  label: 'Todos' },
  { value: 'pending',              label: 'Pendiente' },
  { value: 'partially_approved',   label: 'Parcial Aprobado' },
  { value: 'sent_back',            label: 'Devuelto' },
  { value: 'partially_returned',   label: 'Parcial Devuelto' },
  { value: 'approved',             label: 'Aprobado' },
  { value: 'paid',                 label: 'Pagado' },
  { value: 'rejected',             label: 'Rechazado' },
];

function fmt(amount) {
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: NAVY, color: '#fff', padding: '8px 12px',
      borderRadius: 8, fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{label || payload[0].name}</div>
      <div>{fmt(payload[0].value)}</div>
      {payload[0].payload.count !== undefined && (
        <div style={{ fontSize: 11, opacity: 0.7 }}>{payload[0].payload.count} item{payload[0].payload.count !== 1 ? 's' : ''}</div>
      )}
    </div>
  );
}

const FILTER_LABELS = {
  costCenter: 'Código',
  category: 'Categoría',
  employee: 'Chofer',
  vendor: 'Proveedor',
  supervisor: 'Supervisor(a)',
  glCode: 'Código GL',
  project: 'Proyecto',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { glCodes: glCodesCtx } = useOptions();
  const glCodeMap = useMemo(() => {
    const map = {};
    for (const gl of (glCodesCtx || [])) {
      map[gl.code] = gl.name;
    }
    return map;
  }, [glCodesCtx]);

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [filters, setFilters] = useState({
    costCenter: null,
    category: null,
    employee: null,
    vendor: null,
    supervisor: null,
    glCode: null,
    project: null,
    status: null,
  });
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchReceipts = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await client.get('/api/receipts', { params });
      setReceipts(res.data);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReceipts({ dateFrom, dateTo });
  }, [fetchReceipts, dateFrom, dateTo]);

  useEffect(() => {
    client.get('/api/admin/expense-metrics')
      .then(res => setMetrics(res.data))
      .catch(err => console.error('Failed to load metrics:', err));
  }, []);

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    setDateFrom(df);
    setDateTo(dt);
  }

  function toggleFilter(key, value) {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));
  }

  function clearAllFilters() {
    setFilters({ costCenter: null, category: null, employee: null, vendor: null, supervisor: null, glCode: null, project: null, status: null });
  }

  const hasFilters = Object.values(filters).some(v => v);

  // Filtered receipts based on active cross-filters (item-aware)
  const filteredReceipts = useMemo(() => {
    return receipts.filter(r => {
      if (filters.employee && r.employeeName !== filters.employee) return false;
      if (filters.vendor && (r.vendor || 'Sin Proveedor') !== filters.vendor) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (filters.costCenter) {
        const hasMatch = (r.items || []).some(item => (item.costCenter || 'Sin Asignar') === filters.costCenter);
        if (!hasMatch) return false;
      }
      if (filters.category) {
        const hasMatch = (r.items || []).some(item => (item.category || 'Sin Asignar') === filters.category);
        if (!hasMatch) return false;
      }
      if (filters.supervisor) {
        const hasMatch = (r.items || []).some(item => (item.supervisorName || 'Sin Supervisor') === filters.supervisor);
        if (!hasMatch) return false;
      }
      if (filters.glCode) {
        const hasMatch = (r.items || []).some(item => (item.glCode || 'Sin GL') === filters.glCode);
        if (!hasMatch) return false;
      }
      if (filters.project) {
        const hasMatch = (r.items || []).some(item => (item.projectName || 'Sin Proyecto') === filters.project);
        if (!hasMatch) return false;
      }
      return true;
    });
  }, [receipts, filters]);

  // Flatten all line items from filtered receipts for item-level aggregation
  const filteredItems = useMemo(() => {
    const allItems = [];
    filteredReceipts.forEach(r => {
      (r.items || []).forEach(item => {
        allItems.push({
          ...item,
          receiptId: r.id,
          employeeName: r.employeeName,
          vendor: r.vendor,
          receiptDate: r.date,
          receiptStatus: r.status,
        });
      });
    });
    return allItems;
  }, [filteredReceipts]);

  // Aggregations — at line item level
  const byCostCenter = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.costCenter || 'Sin Asignar';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  const byCategory = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.category || 'Sin Asignar';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  const byEmployee = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.employeeName || 'Desconocido';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  const byVendor = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.vendor || 'Sin Proveedor';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  const bySupervisor = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.supervisorName || 'Sin Supervisor';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  const byGlCode = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const code = item.glCode || 'Sin GL';
      const displayName = glCodeMap[code] || code;
      if (!map[code]) map[code] = { name: displayName, code, total: 0, count: 0 };
      map[code].total += parseFloat(item.amount) || 0;
      map[code].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems, glCodeMap]);

  const byProject = useMemo(() => {
    const map = {};
    filteredItems.forEach(item => {
      const key = item.projectName || 'Sin Proyecto';
      if (!map[key]) map[key] = { name: key, total: 0, count: 0 };
      map[key].total += parseFloat(item.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredItems]);

  // Stacked project data: each bar has petty_cash and credit_card segments
  const byProjectStacked = useMemo(() => {
    const map = {};
    filteredReceipts.forEach(r => {
      (r.items || []).forEach(item => {
        const key = item.projectName || 'Sin Proyecto';
        if (!map[key]) map[key] = { name: key, petty_cash: 0, credit_card: 0, total: 0, count: 0 };
        const amt = parseFloat(item.amount) || 0;
        if (r.paymentMethod === 'petty_cash') {
          map[key].petty_cash += amt;
        } else {
          map[key].credit_card += amt;
        }
        map[key].total += amt;
        map[key].count += 1;
      });
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredReceipts]);

  const PAYMENT_METHOD_LABELS = { credit_card: 'Tarjeta de Crédito', petty_cash: 'Caja Chica' };
  const byPaymentType = useMemo(() => {
    const map = {};
    filteredReceipts.forEach(r => {
      const key = r.paymentMethod || 'Sin Método';
      const label = PAYMENT_METHOD_LABELS[key] || key;
      if (!map[key]) map[key] = { name: label, code: key, total: 0, count: 0 };
      map[key].total += parseFloat(r.amount) || 0;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredReceipts]);

  // Summary stats — totals from line items, averages per receipt
  const totalAmount = useMemo(() =>
    filteredItems.reduce((s, item) => s + (parseFloat(item.amount) || 0), 0),
    [filteredItems],
  );
  const avgAmount = filteredReceipts.length > 0 ? totalAmount / filteredReceipts.length : 0;
  const approvedCount = filteredReceipts.filter(r => r.status === 'approved').length;
  const reviewedCount = filteredReceipts.filter(r => ['approved', 'rejected'].includes(r.status)).length;
  const approvalRate = reviewedCount > 0 ? Math.round((approvedCount / reviewedCount) * 100) : 0;

  // Compact chart height — keep all 6 graphs visible at a glance
  const uniformChartHeight = useMemo(() => {
    const maxLen = Math.max(
      byCostCenter.length, byCategory.length, byEmployee.length,
      byVendor.length, bySupervisor.length, byGlCode.length, byProject.length, 1
    );
    return Math.min(260, Math.max(160, maxLen * 28 + 40));
  }, [byCostCenter.length, byCategory.length, byEmployee.length, byVendor.length, bySupervisor.length, byGlCode.length, byProject.length]);

  if (loading && receipts.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: '#94a3b8', gap: 8 }}>
        <Loader2 size={20} className="spin" /> Cargando dashboard…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 18px' }}>
        Dashboard de Gastos
      </h2>

      {/* Active filter pills */}
      {hasFilters && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          {Object.entries(filters).map(([key, value]) => value && (
            <span key={key} style={{
              background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.3)',
              borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600,
              color: AMBER, display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: 'Inter, sans-serif',
            }}>
              {FILTER_LABELS[key]}: {key === 'glCode' ? (glCodeMap[value] || value) : value}
              <X size={12} style={{ cursor: 'pointer' }}
                onClick={() => setFilters(prev => ({ ...prev, [key]: null }))} />
            </span>
          ))}
          <button onClick={clearAllFilters} style={{
            fontSize: 12, color: '#64748b', background: 'none',
            border: 'none', cursor: 'pointer', textDecoration: 'underline',
            fontFamily: 'Inter, sans-serif',
          }}>
            Limpiar todo
          </button>
        </div>
      )}

      {/* Filters row: status pills + date range */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(({ value, label }) => {
            const active = value === 'all' ? !filters.status : filters.status === value;
            return (
              <button key={value} onClick={() => setFilters(prev => ({
                ...prev,
                status: value === 'all' ? null : (prev.status === value ? null : value),
              }))} style={{
                padding: '6px 14px', fontSize: 12, fontWeight: active ? 700 : 500,
                borderRadius: 20, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                border: active ? `1.5px solid ${AMBER}` : `1px solid ${BORDER_COLOR}`,
                background: active ? 'rgba(217,119,6,0.08)' : '#fff',
                color: active ? AMBER : '#64748b',
                transition: 'all 0.15s',
              }}>
                {label}
              </button>
            );
          })}
        </div>
        <div className="date-range" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Recibos" value={filteredReceipts.length} />
        <StatCard label="Monto Total" value={fmt(totalAmount)} highlight />
        <StatCard label="Gasto Promedio" value={fmt(avgAmount)} />
        <StatCard label="Tasa de Aprobación" value={`${approvalRate}%`} subtitle={`${approvedCount} de ${reviewedCount} revisados`} />
      </div>

      {/* Charts grid */}
      {filteredReceipts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 16, fontWeight: '600', color: '#64748b' }}>No hay datos de gastos para este período</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Intenta ajustar el rango de fechas o limpiar los filtros</div>
        </div>
      ) : (
        <>
          <div className="dashboard-grid">
            {[
              { key: 'employee',   label: 'Por Chofer',      data: byEmployee,   yWidth: 120, filterKey: 'employee',   scrollable: false },
              { key: 'supervisor', label: 'Por Supervisor',  data: bySupervisor, yWidth: 120, filterKey: 'supervisor', scrollable: false },
              /* project chart rendered separately as stacked */
              { key: 'costCenter', label: 'Por Código',      data: byCostCenter, yWidth: 130, filterKey: 'costCenter', scrollable: false },
              { key: 'glCode',     label: 'Por Código GL',   data: byGlCode,     yWidth: 160, filterKey: 'glCode',     scrollable: true  },
              { key: 'vendor',     label: 'Por Proveedor',   data: byVendor,     yWidth: 140, filterKey: 'vendor',     scrollable: true  },
            ].map(({ key, label, data, yWidth, filterKey, scrollable }) => {
              const getFilterValue = (entry) => entry.code || entry.name;
              const getActiveFilter = filters[filterKey];
              const tickFmt = v => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
              const naturalHeight = Math.max(160, data.length * 28 + 40);
              const chartHeight = scrollable ? naturalHeight : uniformChartHeight;
              const chart = (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" orientation={scrollable ? 'top' : 'bottom'} tickFormatter={tickFmt}
                      fontSize={11} tick={{ fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" width={yWidth} fontSize={12} tick={{ fill: NAVY }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="total" radius={[0, 6, 6, 0]} cursor="pointer"
                      onClick={(d) => toggleFilter(filterKey, getFilterValue(d))}>
                      {data.map((entry, i) => (
                        <Cell key={getFilterValue(entry)}
                          fill={getActiveFilter === getFilterValue(entry) ? AMBER : CHART_COLORS[i % CHART_COLORS.length]}
                          opacity={getActiveFilter && getActiveFilter !== getFilterValue(entry) ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
              return (
              <div key={key} style={{ ...bubbleStyle, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: 0 }}>{label}</h3>
                  {scrollable && data.length > 0 && (
                    <span style={{ fontSize: 11, color: '#64748b' }}>{data.length} registros</span>
                  )}
                </div>
                {data.length === 0 ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Sin datos</div>
                ) : scrollable ? (
                  <div style={{ maxHeight: 280, overflowY: 'auto', borderRadius: 8, border: `1px solid ${BORDER_COLOR}` }}>
                    {chart}
                  </div>
                ) : (
                  chart
                )}
              </div>
              );
            })}
          </div>

          {/* Stacked Project Chart (Petty Cash vs Credit Card) */}
          {byProjectStacked.length > 0 && (
            <div style={{ ...bubbleStyle, padding: '14px 16px', marginTop: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                Por Proyecto
              </h3>
              <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 11 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#d97706' }} /> Caja Chica
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#2563eb' }} /> Tarjeta de Crédito
                </span>
              </div>
              <ResponsiveContainer width="100%" height={Math.min(400, Math.max(200, byProjectStacked.length * 32 + 40))}>
                <BarChart data={byProjectStacked} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`}
                    fontSize={11} tick={{ fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="name" width={140} fontSize={12} tick={{ fill: NAVY }} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div style={{
                        background: NAVY, color: '#fff', padding: '8px 12px',
                        borderRadius: 8, fontSize: 13, fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                        {payload.map(p => (
                          <div key={p.dataKey} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill }} />
                            <span>{p.dataKey === 'petty_cash' ? 'Caja Chica' : 'Tarjeta de Crédito'}:</span>
                            <span style={{ fontWeight: 700 }}>{fmt(p.value)}</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: 4, paddingTop: 4, fontWeight: 700 }}>
                          Total: {fmt(payload.reduce((s, p) => s + (p.value || 0), 0))}
                        </div>
                      </div>
                    );
                  }} />
                  <Bar dataKey="petty_cash" stackId="a" fill="#d97706" radius={[0, 0, 0, 0]}
                    cursor="pointer" onClick={(d) => toggleFilter('project', d.name)} />
                  <Bar dataKey="credit_card" stackId="a" fill="#2563eb" radius={[0, 6, 6, 0]}
                    cursor="pointer" onClick={(d) => toggleFilter('project', d.name)} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Payment Type PieChart */}
          {byPaymentType.length > 0 && (
            <div style={{ ...bubbleStyle, padding: '14px 16px', marginTop: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>
                Por Método de Pago
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                <ResponsiveContainer width={260} height={220}>
                  <PieChart>
                    <Pie
                      data={byPaymentType}
                      dataKey="total"
                      nameKey="name"
                      cx="50%" cy="50%"
                      outerRadius={85}
                      innerRadius={40}
                      paddingAngle={3}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {byPaymentType.map((entry, i) => (
                        <Cell key={entry.code} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {byPaymentType.map((entry, i) => (
                    <div key={entry.code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{
                        width: 12, height: 12, borderRadius: 3,
                        background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0,
                      }} />
                      <span style={{ fontWeight: 600, color: NAVY }}>{entry.name}</span>
                      <span style={{ color: '#64748b' }}>{fmt(entry.total)}</span>
                      <span style={{ color: '#94a3b8', fontSize: 11 }}>({entry.count} recibo{entry.count !== 1 ? 's' : ''})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Approval Time Metrics */}
          {metrics && (
            <div style={{ display: 'flex', gap: 14, marginTop: 20, flexWrap: 'wrap' }}>
              {[
                { label: 'Sometido → Revisado', value: metrics.avgToReview, icon: '⏱' },
                { label: 'Sometido → Aprobado', value: metrics.avgToApproved, icon: '✓' },
                { label: 'Sometido → Pagado', value: metrics.avgToPaid, icon: '💰' },
              ].map(({ label, value, icon }) => (
                <div key={label} style={{
                  ...bubbleStyle, flex: '1 1 200px', padding: '16px 20px',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>
                    {value?.label || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {value?.hours != null ? `${value.hours.toFixed(1)} horas` : 'Sin datos'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Detail table */}
          <div style={{ ...bubbleStyle, marginTop: 24, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: 0 }}>
                Detalle de Gastos
                <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12, marginLeft: 8 }}>
                  {filteredReceipts.length} recibo{filteredReceipts.length !== 1 ? 's' : ''}
                </span>
              </h3>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <tr style={{ background: NAVY, color: '#fff' }}>
                    <th style={{ ...thStyle, width: 36, padding: '10px 8px' }}></th>
                    <th style={thStyle}>Proveedor</th>
                    <th style={thStyle}>Chofer</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Monto</th>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>Sometido</th>
                    <th style={thStyle}>Categoría</th>
                    <th style={thStyle}>Centro de Costo</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}>Revisado por</th>
                    <th style={{ ...thStyle, width: 70, textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.slice(0, 100).map(r => {
                    const isExpanded = expandedRows.has(r.id);
                    const hasItems = r.items && r.items.length > 0;
                    return [
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: hasItems ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (!hasItems) return;
                          setExpandedRows(prev => {
                            const next = new Set(prev);
                            if (next.has(r.id)) next.delete(r.id);
                            else next.add(r.id);
                            return next;
                          });
                        }}>
                        <td style={{ ...tdStyle, padding: '10px 8px', width: 36 }}>
                          {hasItems && (
                            isExpanded
                              ? <ChevronDown size={16} style={{ color: AMBER }} />
                              : <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                          )}
                        </td>
                        <td style={tdStyle}>{r.vendor || '—'}</td>
                        <td style={tdStyle}>{r.employeeName || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{r.amount != null ? fmt(r.amount) : '—'}</td>
                        <td style={tdStyle}>{r.date || '—'}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: '#64748b' }}>
                          {r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td style={tdStyle}>{r.category || '—'}</td>
                        <td style={tdStyle}>{r.costCenter || '—'}</td>
                        <td style={tdStyle}>
                          <span style={{
                            background: STATUS_COLORS[r.status]?.bg || '#f1f5f9',
                            color: STATUS_COLORS[r.status]?.text || '#64748b',
                            border: `1px solid ${STATUS_COLORS[r.status]?.border || BORDER_COLOR}`,
                            borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </td>
                        <td style={tdStyle}>{r.reviewerName || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/receipt/${r.id}`); }}
                            title="Ver detalle"
                            style={{
                              background: 'none', border: `1px solid ${BORDER_COLOR}`,
                              borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 11, fontWeight: 600, color: '#475569',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            <Eye size={13} /> Ver
                          </button>
                        </td>
                      </tr>,
                      isExpanded && hasItems && (
                        <tr key={`${r.id}-items`}>
                          <td colSpan={11} style={{ padding: 0, background: '#f8fafc' }}>
                            <div style={{ padding: '8px 16px 12px 52px' }}>
                              {(() => {
                                const itemsByProject = {};
                                for (const item of r.items) {
                                  const key = item.projectName || '(Sin Proyecto)';
                                  if (!itemsByProject[key]) itemsByProject[key] = [];
                                  itemsByProject[key].push(item);
                                }
                                const projectGroups = Object.entries(itemsByProject);
                                return (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                      <tr style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                        <th style={subThStyle}>Descripción</th>
                                        <th style={{ ...subThStyle, textAlign: 'right' }}>Monto</th>
                                        <th style={subThStyle}>Código</th>
                                        <th style={subThStyle}>GL</th>
                                        <th style={subThStyle}>Supervisor</th>
                                        <th style={subThStyle}>Estado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {projectGroups.map(([projectName, projectItems]) => {
                                        const projectTotal = projectItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
                                        const supervisorName = projectItems[0]?.supervisorName || '—';
                                        return (
                                          <Fragment key={projectName}>
                                            <tr>
                                              <td colSpan={6} style={{
                                                padding: '7px 10px', background: '#f0f4ff',
                                                fontWeight: 600, fontSize: 12, color: NAVY,
                                                borderBottom: `1px solid ${BORDER_COLOR}`,
                                              }}>
                                                {projectName}
                                                <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 10 }}>Supervisor: {supervisorName}</span>
                                                <span style={{ float: 'right', fontWeight: 700 }}>{fmt(projectTotal)}</span>
                                              </td>
                                            </tr>
                                            {projectItems.map((item, idx) => (
                                              <tr key={item.id || idx} style={{ borderBottom: idx < projectItems.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                                <td style={subTdStyle}>{item.description || '—'}</td>
                                                <td style={{ ...subTdStyle, textAlign: 'right', fontWeight: 600 }}>{item.amount != null ? fmt(item.amount) : '—'}</td>
                                                <td style={subTdStyle}>{item.costCenter || '—'}</td>
                                                <td style={subTdStyle}>{item.glCode || '—'}</td>
                                                <td style={subTdStyle}>{item.supervisorName || '—'}</td>
                                                <td style={subTdStyle}>
                                                  <span style={{
                                                    background: STATUS_COLORS[item.status]?.bg || '#f1f5f9',
                                                    color: STATUS_COLORS[item.status]?.text || '#64748b',
                                                    border: `1px solid ${STATUS_COLORS[item.status]?.border || BORDER_COLOR}`,
                                                    borderRadius: 20, padding: '1px 8px', fontSize: 10, fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                  }}>
                                                    {STATUS_LABEL[item.status] || item.status}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </Fragment>
                                        );
                                      })}
                                      <tr>
                                        <td style={{ ...subTdStyle, fontWeight: 700, color: NAVY, borderTop: `1px solid ${BORDER_COLOR}` }}>Total</td>
                                        <td style={{ ...subTdStyle, textAlign: 'right', fontWeight: 700, color: NAVY, borderTop: `1px solid ${BORDER_COLOR}` }}>
                                          {fmt(r.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))}
                                        </td>
                                        <td colSpan={4} style={{ borderTop: `1px solid ${BORDER_COLOR}` }} />
                                      </tr>
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                  {filteredReceipts.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8', padding: 40 }}>
                        Ningún recibo coincide con los filtros actuales
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {filteredReceipts.length > 100 && (
              <div style={{ padding: '12px 20px', fontSize: 12, color: '#94a3b8', borderTop: `1px solid ${BORDER_COLOR}` }}>
                Mostrando los primeros 100 de {filteredReceipts.length} recibos. Usa los filtros para reducir resultados.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 14px', fontSize: 12, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', background: NAVY, color: '#fff' };
const tdStyle = { padding: '10px 14px', color: '#1e293b' };
const subThStyle = { padding: '6px 10px', fontSize: 11, fontWeight: 600, textAlign: 'left', color: '#64748b', whiteSpace: 'nowrap' };
const subTdStyle = { padding: '6px 10px', color: '#475569', fontSize: 12 };
